/**
 * EmbeddingsManager - Client-side word embeddings for semantic similarity
 * 
 * Loads quantized GloVe embeddings and provides similarity calculations
 * to find "bridge words" that help users progress toward the goal.
 */

import { logger } from '@mental-mint/shared-utils';

const LOG_PREFIX = '[Embeddings]';

export interface EmbeddingsMetadata {
  version: number;
  dimensions: number;
  wordCount: number;
  quantization: {
    min: number;
    max: number;
    scale: number;
  };
  words: string[];
}

/**
 * Manages word embeddings for semantic similarity calculations
 */
export class EmbeddingsManager {
  private vectors: Map<string, Int8Array> = new Map();
  private metadata: EmbeddingsMetadata | null = null;
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;

  /**
   * Load embeddings from URLs
   */
  async load(binaryUrl: string, metadataUrl: string): Promise<void> {
    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Return immediately if already loaded
    if (this.isLoaded) {
      return Promise.resolve();
    }

    this.loadPromise = this._loadEmbeddings(binaryUrl, metadataUrl);
    return this.loadPromise;
  }

  private async _loadEmbeddings(binaryUrl: string, metadataUrl: string): Promise<void> {
    try {
      const startTime = performance.now();
      logger.log(LOG_PREFIX, 'Loading embeddings...');

      // Load metadata and binary in parallel
      const [metadataResponse, binaryResponse] = await Promise.all([
        fetch(metadataUrl),
        fetch(binaryUrl)
      ]);

      if (!metadataResponse.ok || !binaryResponse.ok) {
        throw new Error('Failed to fetch embeddings files');
      }

      // Parse metadata
      this.metadata = await metadataResponse.json();
      logger.log(LOG_PREFIX, `Loaded metadata: ${this.metadata.wordCount} words, ${this.metadata.dimensions}d`);

      // Parse binary data
      const arrayBuffer = await binaryResponse.arrayBuffer();
      this._parseBinaryData(arrayBuffer);

      const loadTime = (performance.now() - startTime).toFixed(0);
      logger.log(LOG_PREFIX, `Loaded ${this.vectors.size} embeddings in ${loadTime}ms`);

      this.isLoaded = true;
    } catch (error) {
      console.error('❌ EMBEDDINGS: Load failed:', error);
      logger.error(LOG_PREFIX, 'Failed to load embeddings:', error);
      throw error;
    }
  }

  /**
   * Parse binary embeddings data
   */
  private _parseBinaryData(arrayBuffer: ArrayBuffer): void {
    if (!this.metadata) {
      throw new Error('Metadata must be loaded before parsing binary data');
    }

    const buffer = new DataView(arrayBuffer);
    let offset = 0;

    // Read header
    const wordCount = buffer.getUint32(offset, true);
    offset += 4;
    const dimensions = buffer.getUint32(offset, true);
    offset += 4;

    if (wordCount !== this.metadata.wordCount || dimensions !== this.metadata.dimensions) {
      throw new Error('Binary data does not match metadata');
    }

    // Read vectors
    const { words } = this.metadata;
    for (let i = 0; i < wordCount; i++) {
      const word = words[i];
      const vector = new Int8Array(dimensions);

      for (let d = 0; d < dimensions; d++) {
        vector[d] = buffer.getInt8(offset);
        offset++;
      }

      this.vectors.set(word, vector);
    }
  }

  /**
   * Get vector for a word
   */
  getVector(word: string): Int8Array | null {
    if (!this.isLoaded) {
      logger.warn(LOG_PREFIX, 'Embeddings not loaded yet');
      return null;
    }
    if (!word || typeof word !== 'string') {
      logger.warn(LOG_PREFIX, `Invalid word parameter: ${word}`);
      return null;
    }
    return this.vectors.get(word.toLowerCase()) || null;
  }

  /**
   * Calculate cosine similarity between two vectors
   */
  private _cosineSimilarity(vec1: Int8Array, vec2: Int8Array): number {
    let dotProduct = 0;
    let norm1 = 0;
    let norm2 = 0;

    for (let i = 0; i < vec1.length; i++) {
      dotProduct += vec1[i] * vec2[i];
      norm1 += vec1[i] * vec1[i];
      norm2 += vec2[i] * vec2[i];
    }

    if (norm1 === 0 || norm2 === 0) {
      return 0;
    }

    return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
  }

  /**
   * Calculate similarity between two words
   */
  similarity(word1: string, word2: string): number | null {
    if (!word1 || !word2 || typeof word1 !== 'string' || typeof word2 !== 'string') {
      logger.warn(LOG_PREFIX, `Invalid word parameters: word1=${word1}, word2=${word2}`);
      return null;
    }
    
    const vec1 = this.getVector(word1);
    const vec2 = this.getVector(word2);

    if (!vec1 || !vec2) {
      return null;
    }

    return this._cosineSimilarity(vec1, vec2);
  }

  /**
   * Find "bridge words" that are semantically between current word and goal word
   * 
   * CRITICAL LOGIC:
   * - Bridge words should feel RELATED to current word (closer to current than to goal)
   * - Bridge words should make MEANINGFUL PROGRESS toward goal (minimum 0.1 similarity improvement)
   * - Exception: If current is already very close to goal (sim > 0.8), allow goal word itself
   * 
   * @param currentWord - The current word
   * @param goalWord - The target goal word
   * @param candidateWords - List of candidate words to consider
   * @param count - Number of bridge words to return
   * @returns Array of bridge words sorted by how well they balance relatedness and progress
   */
  findBridgeWords(
    currentWord: string,
    goalWord: string,
    candidateWords: string[],
    count: number = 3
  ): string[] {
    if (!this.isLoaded) {
      logger.warn(LOG_PREFIX, 'Embeddings not loaded, cannot find bridge words');
      return [];
    }

    const currentVec = this.getVector(currentWord);
    const goalVec = this.getVector(goalWord);

    if (!currentVec || !goalVec) {
      logger.warn(LOG_PREFIX, `Missing vectors for current="${currentWord}" (found: ${!!currentVec}) or goal="${goalWord}" (found: ${!!goalVec})`);
      return [];
    }

    const currentToGoalSim = this._cosineSimilarity(currentVec, goalVec);
    
    // THRESHOLDS - will be progressively relaxed if no bridge words found
    const VERY_CLOSE_THRESHOLD = 0.92; // If current is this close to goal, allow goal as bridge
    let minProgress = 0.2; // Bridge word must be at least this much closer to goal than current is
    let maxProgress = 0.5; // Bridge word must not be too much closer (max step size)
    let minSimilarityToCurrent = 0.9; // Bridge word must be at least this similar to current word
    const MIN_DIVERSITY_THRESHOLD = 0.92; // Result's words should be <X% similar to each other
    
    // If word is close enough to goal, include goal in results
    const resultSet = new Set<string>();
    if (currentToGoalSim >= VERY_CLOSE_THRESHOLD) {
        resultSet.add(goalWord);
    }

    // Progressive relaxation loop - try stricter criteria first, then relax if needed
    let attempt = 0;
    const MAX_ATTEMPTS = 20; // Increased to ensure we find diverse words
    
    while (resultSet.size < count && attempt < MAX_ATTEMPTS) {
      if (attempt > 0) {
        // Relax search criteria progressively
        minSimilarityToCurrent = Math.max(0, minSimilarityToCurrent - 0.02);
        maxProgress = Math.min(1, maxProgress + 0.1);
        minProgress = Math.max(-0.03, minProgress - 0.05); // Allow negative progress (moving away from goal)
        logger.debug(LOG_PREFIX, `Relaxing criteria (attempt ${attempt}): minSimToCurrent=${minSimilarityToCurrent.toFixed(2)}, minProgress=${minProgress.toFixed(2)}, maxProgress=${maxProgress.toFixed(2)}`);
      }

      // Score each candidate word
      const scored = candidateWords
      .map(word => {
        const vec = this.getVector(word);
        if (!vec) return null;

        const simToCurrent = this._cosineSimilarity(vec, currentVec);
        const simToGoal = this._cosineSimilarity(vec, goalVec);

        // Calculate progress: how much closer to goal is this word compared to current?
        const progress = simToGoal - currentToGoalSim;

        // Combined score: prioritize words that are similar to current AND make progress
        const score = simToCurrent + progress;

        return {
          word,
          score,
          simToGoal,
          simToCurrent,
          progress,
          currentToGoalSim
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
       // Exclude current word
      .filter((item): item is NonNullable<typeof item> => item.word !== currentWord)
      // Exclude goal word (it will have been added earlier if close enough)
      .filter((item): item is NonNullable<typeof item> => item.word !== goalWord)
      // Exclude already selected words
      .filter(item => !resultSet.has(item.word))
      .filter(item => {
        // CRITICAL 1: Bridge word must be SIMILAR to current word
        const similarToCurrent = item.simToCurrent >= minSimilarityToCurrent;
        
        // CRITICAL 2: Bridge word must make MEANINGFUL but NOT EXCESSIVE PROGRESS toward goal
        // Must be at least minProgress closer but not more than maxProgress closer
        const makesProgress = item.progress >= minProgress && item.progress <= maxProgress;
        
        // Exception: If current is already very close to goal, relax similarity requirement
        // In this case, just require any progress toward goal
        if (item.currentToGoalSim >= VERY_CLOSE_THRESHOLD) {
          const anyProgress = item.simToGoal > item.currentToGoalSim;
          return anyProgress;
        }
        
        // Normal case: must be similar to current AND make meaningful progress
        const isValid = similarToCurrent && makesProgress;
        return isValid;
      })
      .sort((a, b) => b.score - a.score);

      // Add new words to result set with diversity filtering
      const wordsNeeded = count - resultSet.size;
      const newWords: typeof scored = [];
      
      for (const candidate of scored) {
        if (newWords.length >= wordsNeeded) break;
        
        // Check diversity: ensure this word isn't too similar to already selected words
        const candidateVec = this.getVector(candidate.word);
        if (!candidateVec) continue;
        
        let isDiverse = true;
        let rejectionReason = '';
        let maxSimilarity = 0;
        
        // Check against words already in resultSet (from previous attempts)
        for (const alreadySelected of resultSet) {
          const selectedVec = this.getVector(alreadySelected);
          if (selectedVec) {
            const similarity = this._cosineSimilarity(candidateVec, selectedVec);
            maxSimilarity = Math.max(maxSimilarity, similarity);
            if (similarity > MIN_DIVERSITY_THRESHOLD) {
              isDiverse = false;
              rejectionReason = `too similar to "${alreadySelected}" (${similarity.toFixed(3)})`;
              break;
            }
          }
        }
        
        // Also check against words selected in this attempt
        if (isDiverse) {
          for (const selected of newWords) {
            const selectedVec = this.getVector(selected.word);
            if (selectedVec) {
              const similarity = this._cosineSimilarity(candidateVec, selectedVec);
              maxSimilarity = Math.max(maxSimilarity, similarity);
              if (similarity > MIN_DIVERSITY_THRESHOLD) {
                isDiverse = false;
                rejectionReason = `too similar to "${selected.word}" (${similarity.toFixed(3)})`;
                break;
              }
            }
          }
        }
        
        if (isDiverse) {
          newWords.push(candidate);
          // Log acceptance with max similarity to other selected words
          if (attempt === 0 && newWords.length <= 5) {
            console.log(`  ✅ Accepted "${candidate.word}" as bridge word (max similarity to others: ${maxSimilarity.toFixed(3)})`);
          }
        } else if (attempt === 0 && resultSet.size + newWords.length < 5) {
          // Log rejections during first attempt for debugging
          console.log(`  ⚠️ Rejected "${candidate.word}": ${rejectionReason}`);
        }
      }
      
      newWords.forEach(item => resultSet.add(item.word));
      
      attempt++;
    }
    
    // Convert Set to Array for return
    const result = Array.from(resultSet);
    
    // Log selected bridge words with cosine similarities (useful for debugging on page load)
    if (result.length > 0) {
      const finalScored = candidateWords
        .map(word => {
          if (!result.includes(word)) return null;
          const vec = this.getVector(word);
          if (!vec) return null;
          const simToCurrent = this._cosineSimilarity(vec, currentVec);
          const simToGoal = this._cosineSimilarity(vec, goalVec);
          return { word, simToCurrent, simToGoal };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);
      
      console.log(`Bridge words (${result.length}) ["${currentWord}" →goal "${goalWord}": ${currentToGoalSim.toFixed(3)}]: ${finalScored.map(item => `${item.word} (→current: ${item.simToCurrent.toFixed(2)}, →goal: ${item.simToGoal.toFixed(2)})`).join(', ')}`);
    }
    
    return result;
  }

  /**
   * Check if embeddings are loaded
   */
  get loaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Get number of loaded vectors
   */
  get size(): number {
    return this.vectors.size;
  }
}

// Singleton instance
let embeddingsManager: EmbeddingsManager | null = null;

/**
 * Get the singleton embeddings manager instance
 */
export function getEmbeddingsManager(): EmbeddingsManager {
  if (!embeddingsManager) {
    embeddingsManager = new EmbeddingsManager();
  }
  return embeddingsManager;
}
